import { List, ListItem } from "@solomon-apps/shared/src/components/Data/List";
import { IndicationUnit } from "@solomon-apps/shared/src/components/Metrics/IndicationUnit";
import { Text } from "@solomon-apps/shared/src/components/ui/text";
import React, { useMemo, useState } from "react";

import {
    SortableColHead,
    type SortDirection
} from "@/pages/Positions/components/SortableColHead";
import { truncateAddress } from "@/pages/shared/format";

import type { UserRow } from "../userRows";

/**
 * Shared column template for the Users table — used by both the header row
 * and each `ListItem` row so the columns line up:
 * status · name · address · balance · APY · earned · pending.
 *
 * Mirrors the `ROW_GRID` pattern in PositionsPage: a fixed status-dot
 * column, two flexible text columns (name + address), then right-aligned
 * numeric columns.
 */
const ROW_GRID =
    "grid grid-cols-[16px_minmax(120px,1.2fr)_minmax(140px,1.4fr)_minmax(120px,1.1fr)_minmax(72px,0.8fr)_minmax(120px,1.1fr)_minmax(120px,1.1fr)] items-center gap-4";

type SortColumn = "name" | "address" | "balance" | "apy" | "earned" | "pending";

function fmtUsd(value: number): string {
    return value.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });
}

/**
 * Per-user settlement result → IndicationUnit `status` state. `Rewarded`
 * reads as success; `NotRewarded` as pending (needs attention); a user with
 * no settlement history (null result) reads as disabled.
 */
function statusState(
    result: UserRow["result"]
): "success" | "pending" | "disabled" {
    if (result === "Rewarded") return "success";
    if (result === "NotRewarded") return "pending";
    return "disabled";
}

/**
 * YaaS Client Console — Users (master/list) view.
 *
 * A full-width sortable table of the client's users, mirroring the Positions
 * page: a sortable column-header row over the shared `ROW_GRID`, then
 * `<List selectable="single">` rows each with a leading status dot and
 * right-aligned numeric cells. Clicking a row opens that user's existing
 * detail view (the parent sets the `account` search param).
 */
export function UsersTable({
    users,
    onSelect
}: {
    users: UserRow[];
    onSelect: (uuid: string) => void;
}): React.ReactElement {
    const [sortColumn, setSortColumn] = useState<SortColumn>("balance");
    const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

    const handleSort = (column: SortColumn): void => {
        if (column === sortColumn) {
            setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
        } else {
            setSortColumn(column);
            setSortDirection("asc");
        }
    };

    const sortedUsers = useMemo(() => {
        const copy = [...users];
        const cmp = (a: UserRow, b: UserRow): number => {
            switch (sortColumn) {
                case "name":
                    return a.name.localeCompare(b.name);
                case "address":
                    return a.address.localeCompare(b.address);
                case "balance":
                    return a.balance - b.balance;
                case "apy":
                    // Null (no rate) sorts below every known rate.
                    if (a.apyPct === null || b.apyPct === null) {
                        return (
                            (a.apyPct === null ? 0 : 1) -
                            (b.apyPct === null ? 0 : 1)
                        );
                    }
                    return a.apyPct - b.apyPct;
                case "earned":
                    return a.earned - b.earned;
                case "pending":
                    return a.pending - b.pending;
            }
        };
        copy.sort((a, b) => (sortDirection === "asc" ? cmp(a, b) : -cmp(a, b)));
        return copy;
    }, [users, sortColumn, sortDirection]);

    if (sortedUsers.length === 0) {
        return (
            <div className="px-6 py-8">
                <Text variant="small" className="text-foreground-muted">
                    No users in this view.
                </Text>
            </div>
        );
    }

    return (
        <div className="border-t border-line-base">
            {/* Column header row */}
            <div className={`${ROW_GRID} px-6 py-2 border-b border-line-base`}>
                <span aria-hidden />
                <SortableColHead
                    label="Name"
                    columnKey="name"
                    activeColumn={sortColumn}
                    direction={sortDirection}
                    onSort={handleSort}
                />
                <SortableColHead
                    label="Address"
                    columnKey="address"
                    activeColumn={sortColumn}
                    direction={sortDirection}
                    onSort={handleSort}
                />
                <SortableColHead
                    label="Balance"
                    columnKey="balance"
                    activeColumn={sortColumn}
                    direction={sortDirection}
                    onSort={handleSort}
                    align="right"
                />
                <SortableColHead
                    label="APY"
                    columnKey="apy"
                    activeColumn={sortColumn}
                    direction={sortDirection}
                    onSort={handleSort}
                    align="right"
                />
                <SortableColHead
                    label="Earned"
                    columnKey="earned"
                    activeColumn={sortColumn}
                    direction={sortDirection}
                    onSort={handleSort}
                    align="right"
                />
                <SortableColHead
                    label="Pending"
                    columnKey="pending"
                    activeColumn={sortColumn}
                    direction={sortDirection}
                    onSort={handleSort}
                    align="right"
                />
            </div>

            <List selectable="single" selected="" onSelect={onSelect}>
                {sortedUsers.map((u) => (
                    <ListItem
                        key={u.uuid}
                        itemKey={u.uuid}
                        padding="px-6 py-3"
                        gap="gap-0"
                    >
                        <div className={`${ROW_GRID} w-full`}>
                            <IndicationUnit
                                variant="status"
                                state={statusState(u.result)}
                                size={7}
                            />
                            <Text
                                variant="fine-print"
                                render={<span />}
                                className="text-foreground-base truncate"
                            >
                                {u.name || "—"}
                            </Text>
                            <Text
                                variant="fine-print"
                                render={<span />}
                                className="text-foreground-muted font-numeric truncate"
                            >
                                {truncateAddress(u.address)}
                            </Text>
                            <NumCell>{fmtUsd(u.balance)}</NumCell>
                            <NumCell>
                                {u.apyPct === null
                                    ? "—"
                                    : `${u.apyPct.toFixed(2)}%`}
                            </NumCell>
                            <NumCell>{fmtUsd(u.earned)}</NumCell>
                            <NumCell>{fmtUsd(u.pending)}</NumCell>
                        </div>
                    </ListItem>
                ))}
            </List>
        </div>
    );
}

/** Right-aligned numeric table cell — fine-print, tabular. */
function NumCell({
    children
}: {
    children: React.ReactNode;
}): React.ReactElement {
    return (
        <Text
            variant="fine-print"
            render={<span />}
            className="text-foreground-base font-numeric tabular-nums text-right"
        >
            {children}
        </Text>
    );
}
